import { createContext, useContext, useEffect, useRef, useState, ReactNode } from "react";
import { useQuery } from "@tanstack/react-query";
import { getMyOrganizations, getPostableOrganizations, type UserOrganization } from "@tickethall/core";
import { safeStorage } from "@/lib/storage";
import { useAuth } from "@/contexts/AuthContext";
import { supabase } from "@/integrations/supabase/client";

interface ActiveContext {
  type: "personal" | "organization";
  organizationId: string | null;
  organizationName: string | null;
  organizationSlug: string | null;
  organizationLogo: string | null;
}
interface OrganizationContextType {
  activeContext: ActiveContext;
  setActiveContext: (ctx: ActiveContext) => void;
  organizations: UserOrganization[];
  postableOrganizations: UserOrganization[];
  isLoading: boolean;
  refetch: () => void;
}
const STORAGE_KEY = "th_active_posting_context";
const PERSONAL: ActiveContext = { type: "personal", organizationId: null, organizationName: null, organizationSlug: null, organizationLogo: null };
const fromOrg = (org: UserOrganization): ActiveContext => ({ type: "organization", organizationId: org.org_id, organizationName: org.org_name, organizationSlug: org.org_slug, organizationLogo: org.org_logo });
function getStoredContext(userId?: string): ActiveContext {
  if (!userId) return PERSONAL;
  try {
    const stored = JSON.parse(safeStorage.get(`${STORAGE_KEY}:${userId}`) || "null");
    if (stored?.type === "organization" && typeof stored.organizationId === "string") return stored;
  } catch { /* Invalid storage falls back to personal. */ }
  return PERSONAL;
}
const OrganizationContext = createContext<OrganizationContextType>({ activeContext: PERSONAL, setActiveContext: () => {}, organizations: [], postableOrganizations: [], isLoading: false, refetch: () => {} });
export const useOrganization = () => useContext(OrganizationContext);

export function OrganizationProvider({ children }: { children: ReactNode }) {
  const { user } = useAuth();
  const userId = user?.id;
  const [selection, setSelection] = useState<{ userId?: string; context: ActiveContext }>({ context: PERSONAL });
  const initializedUser = useRef<string | null>(null);
  const activeContext = selection.userId === userId ? selection.context : PERSONAL;
  const { data: organizations = [], isPending: orgsPending, isSuccess: orgsReady, refetch } = useQuery({
    queryKey: ["my-organizations", userId], queryFn: getMyOrganizations, enabled: !!userId,
  });
  const { data: postableOrganizations = [] } = useQuery({
    queryKey: ["postable-organizations", userId], queryFn: getPostableOrganizations, enabled: !!userId,
  });
  const activeOrgs = organizations.filter(org => org.member_status === "active");
  const orgIds = activeOrgs.map(org => org.org_id).sort();
  const counts = useQuery({
    queryKey: ["posting-context-event-counts", userId, orgIds],
    enabled: !!userId && orgsReady,
    refetchOnWindowFocus: false,
    queryFn: async () => {
      // Exact HEAD counts avoid downloading events or the default 1000-row limit.
      const results = await Promise.all([
        supabase.from("events").select("id", { count: "exact", head: true }).eq("producer_id", userId!).is("organization_id", null),
        ...orgIds.map(id => supabase.from("events").select("id", { count: "exact", head: true }).eq("organization_id", id)),
      ]);
      for (const result of results) if (result.error) throw result.error;
      return results.map(result => result.count ?? 0);
    },
    retry: 1,
  });

  const setActiveContext = (context: ActiveContext) => {
    if (!userId) return;
    initializedUser.current = userId; // A manual choice wins over an in-flight count.
    setSelection({ userId, context });
    safeStorage.set(`${STORAGE_KEY}:${userId}`, JSON.stringify(context));
  };
  useEffect(() => {
    if (!userId) {
      initializedUser.current = null;
      setSelection({ context: PERSONAL });
    }
  }, [userId]);

  useEffect(() => {
    if (!userId || !orgsReady || initializedUser.current === userId || counts.isFetching || (!counts.isSuccess && !counts.isError)) return;
    const stored = getStoredContext(userId);
    const storedOrg = activeOrgs.find(org => org.org_id === stored.organizationId);
    let context = storedOrg ? fromOrg(storedOrg) : PERSONAL;
    if (counts.isSuccess && counts.data) {
      const candidates = [PERSONAL, ...orgIds.map(id => fromOrg(activeOrgs.find(org => org.org_id === id)!))];
      const highest = Math.max(...counts.data);
      const previousIndex = candidates.findIndex(candidate => candidate.organizationId === context.organizationId);
      // Stable tie: previous valid context, then personal, then organization ID order.
      const winner = counts.data[previousIndex] === highest ? previousIndex : counts.data.findIndex(count => count === highest);
      context = candidates[winner];
    }
    initializedUser.current = userId;
    setSelection({ userId, context });
    safeStorage.set(`${STORAGE_KEY}:${userId}`, JSON.stringify(context));
  }, [userId, orgsReady, counts.isSuccess, counts.isError, counts.isFetching, counts.data, activeOrgs, orgIds]);

  useEffect(() => {
    if (!userId || !orgsReady || selection.userId !== userId || activeContext.type !== "organization") return;
    const org = activeOrgs.find(org => org.org_id === activeContext.organizationId);
    if (!org) {
      setSelection({ userId, context: PERSONAL });
      safeStorage.set(`${STORAGE_KEY}:${userId}`, JSON.stringify(PERSONAL));
    }
  }, [userId, orgsReady, activeOrgs, selection.userId, activeContext.type, activeContext.organizationId]);

  return <OrganizationContext.Provider value={{
    activeContext, setActiveContext, organizations, postableOrganizations,
    isLoading: !!userId && (orgsPending || (orgsReady && (counts.isPending || counts.isFetching) && initializedUser.current !== userId)), refetch,
  }}>{children}</OrganizationContext.Provider>;
}
